"use client"

import styled from "@emotion/styled"
import { useLocale, useTranslations } from "next-intl"
import Link from "next/link"
import { ImageContainer, LeftArrow, NewText } from "ui"
import { sharedColors } from "ui/theme/colors"
import { articlesClick } from "utils"

const StyledLink = styled(Link)`
  text-decoration: none;
  display: flex;
  width: 100%;
  height: 100%;
  ${(props) => props.theme.breakpoints.down(375)} {
    max-width: 325px;
  }
`

const StyledContainer = styled.div<{ $desktopShadow?: boolean; $clickable?: boolean }>`
  border-radius: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  padding: 16px;
  & h2,
  & h1 {
    display: -webkit-box; /* For iOS and Safari */
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  box-shadow: 0px 4px 12px 0px #00000028;
  ${(props) => props.theme.breakpoints.up("md")} {
    border-radius: ${(props) => (props.$desktopShadow ? "16px" : "0")};
    box-shadow: ${(props) => (props.$desktopShadow ? "0px 4px 12px 0px #00000028" : "none")};
    padding: ${(props) => (props.$desktopShadow ? "16px" : "0")};
  }
`

const StyledBottom = styled.div`
  margin-top: auto;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledLinkButton = styled.div<{ $reversed?: boolean }>`
  display: flex;
  gap: 8px;
  align-items: center;
  width: max-content;
  font-size: 20px;
  font-weight: 600;
  line-height: 29px;
  color: black;
  text-decoration: none;
  margin-top: 8px;
  svg {
    transform: ${(props) => (props.$reversed ? "rotate(180deg)" : "none")};
  }
`

type Props = {
  logo: string
  title: string
  subTitle: string
  date: string
  imageDesktop: string
  imageMobile: string
  content: string
  link: string
  associationText: any
  desktopShadow?: boolean
  hasLinkButton?: boolean
}

export function ArticleItem({
  title,
  subTitle,
  date,
  imageDesktop,
  imageMobile,
  link,
  associationText,
  desktopShadow,
  hasLinkButton,
}: Props) {
  const locale = useLocale()
  const t = useTranslations("Articles")

  if (!title || !date || !link || (!imageDesktop && !imageMobile)) {
    return null
  }

  // const isDesktop = useIsDesktop()
  const ndate = new Date(date)
  const yyyy = ndate.getFullYear()
  const mm = ndate.getMonth() + 1
  const dd = ndate.getDate()

  const formattedDate = dd + "/" + mm + "/" + yyyy

  // const onShare = async () => {
  //   const shareData = {
  //     title: title || "",
  //     url: link,
  //     // url:
  //     //   window.location.href +
  //     //   "/" +
  //     //   `${locale === "en" ? "en" : ""}` +
  //     //   job.orderId,
  //   }
  //   if (navigator.share && navigator.canShare(shareData)) {
  //     try {
  //       await navigator.share(shareData)
  //     } catch (err) {
  //       console.error(err)
  //     }
  //   } else {
  //     navigator.clipboard.writeText(shareData.url)
  //   }
  // }

  const articleContent = (
    <>
      <ImageContainer
        desktopUrl={imageDesktop}
        mobileUrl={imageDesktop}
        height={244}
        mobileHeight={"auto"}
        borderRadius={8}
        additionalImgProps={{
          role: "presentation",
          "aria-hidden": "true",
        }}
        mobileAspectRatio={"293 / 244"}
      />

      <NewText
        desktopFontSize={20}
        desktopFontWeight="600"
        desktopLineHeight="29px"
        mobileFontSize={18}
        mobileFontWeight="600"
        mobileLineHeight={"26px"}
        component="h3"
        color={"black"}
        marginTop={"8px"}
        height={{ md: "87px" }}
        letterSpacing={0}
      >
        {title}
      </NewText>
      <NewText
        desktopFontSize={18}
        desktopFontWeight="400"
        desktopLineHeight="26px"
        mobileFontSize={16}
        mobileFontWeight="400"
        mobileLineHeight={"23px"}
        component="p"
        color={"black"}
        marginTop={"8px"}
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {subTitle.trim()}
      </NewText>

      <StyledBottom>
        {associationText ? (
          <NewText
            desktopFontSize={16}
            desktopFontWeight="400"
            desktopLineHeight="23px"
            mobileFontSize={14}
            mobileFontWeight="400"
            mobileLineHeight={"20px"}
            component="p"
            color={sharedColors.gray}
          >
            {associationText}
          </NewText>
        ) : (
          <span />
        )}

        <NewText
          desktopFontSize={16}
          desktopFontWeight="400"
          desktopLineHeight="23px"
          mobileFontSize={14}
          mobileFontWeight="400"
          mobileLineHeight={"20px"}
          component="p"
          color={sharedColors.gray}
        >
          {formattedDate}
        </NewText>
      </StyledBottom>
    </>
  )

  return (
    <StyledLink href={link}>
      <StyledContainer
        onClick={() => articlesClick(title)}
        $desktopShadow={desktopShadow}
        $clickable
      >
        {articleContent}
        {hasLinkButton && (
          <StyledLinkButton $reversed={locale === "en"}>
            {t("readMore")}
            <LeftArrow />
          </StyledLinkButton>
        )}
      </StyledContainer>
    </StyledLink>
  )
}

export default ArticleItem
